import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { LoggerService } from 'src/app/core/services/logger.service';
import { SdkAcdAgent } from 'src/app/data/CXOne/sdk-acd/sdk-acd.service';
import { SdkDigitalService } from 'src/app/data/CXOne/sdk-digital/sdk-digital.service';
import { VoiceCallRingingEventDTO } from '../domain/ContactCenter/VoiceCallRingingEventDTO';
import { DigitalInteractionEventDTO } from '../domain/ContactCenter/DigitalInteractionEventDTO';

const VOICE_STATUS_RINGING = 'ringing';
const VOICE_STATUS_INCOMING = 'incoming';
const VOICE_STATUS_DISCONNECTED = 'disconnected';

const DIGITAL_EVENT_CASE_ASSIGNED = 'CaseInboxAssigned';
const DIGITAL_EVENT_CASE_UNASSIGNED = 'CaseInboxUnassigned';
const DIGITAL_EVENT_MESSAGE_ADDED = 'MessageAddedIntoCase';

const CUSTOMER_MESSAGE_DIRECTION = 'inbound';

interface VoiceContactUpdate {
  contactID?: string;
  status?: string;
  ani?: string;
  isInbound?: boolean;
}

interface DigitalContactUpdate {
  caseId?: string;
  eventDetails?: { eventType?: string };
  isAssignedToAgentInbox?: boolean;
  isCaseAssigned?: boolean;
  messages?: { id?: string; direction?: string }[];
}

/**
 * PF RESIZE POC - Detecta a chegada de interações para trazer o CIF para docked.
 *
 * Assina os SDKs direto (`SdkAcdAgent` e `SdkDigitalService`), sem passar pelo
 * `ContactCenterPFService`, que é compartilhado com o `pessoa-fisica` de
 * produção. Os gatilhos aqui são mais cedo do que os daquele serviço:
 *
 * - Voz: `ringing`/`incoming`, antes do atendimento — o `ContactCenterPFService`
 *   descarta esses status e só expõe `Answered`.
 * - Digital: `CaseInboxAssigned` da própria SDK e mensagem nova do cliente. O
 *   `onDigitalSessionEvent$` só recebe `Assigned` via screenPop
 *   `ATTACHESDYNAMICS` do script do CXOne.
 */
@Injectable()
export class InteractionDockResizePocService {
  public onVoiceCallRingingEvent$ = new Subject<VoiceCallRingingEventDTO>();
  public onDigitalInteractionEvent$ = new Subject<DigitalInteractionEventDTO>();

  /** Evita reemitir o toque, que chega repetido para o mesmo contato. */
  private ringingContactIds = new Set<string>();
  /** Garante um único dock por atribuição de caso digital. */
  private dockedDigitalCaseIds = new Set<string>();
  /** Evita dockar duas vezes pela mesma mensagem do cliente. */
  private lastDockedMessageIdByCase = new Map<string, string>();
  private isStarted = false;

  constructor(
    private logger: LoggerService,
    private sdkAcdAgent: SdkAcdAgent,
    private sdkDigital: SdkDigitalService,
  ) {}

  public start(): void {
    if (this.isStarted) {
      return;
    }

    this.isStarted = true;

    this.sdkAcdAgent.voiceContactUpdate$.subscribe(
      (voiceContact: VoiceContactUpdate) => {
        this.handleVoiceContact(voiceContact);
      },
    );

    this.sdkDigital.onDigitalContactEvent$.subscribe(
      (digitalContact: DigitalContactUpdate) => {
        this.handleDigitalContact(digitalContact);
      },
    );

    this.logger.info('PF RESIZE POC - Monitoramento de novas interações ativo.');
  }

  private handleVoiceContact(voiceContact: VoiceContactUpdate): void {
    const contactId = voiceContact?.contactID || '';

    if (!contactId) {
      return;
    }

    const status = (voiceContact.status || '').toLowerCase();

    if (status === VOICE_STATUS_DISCONNECTED) {
      this.ringingContactIds.delete(contactId);
      return;
    }

    const isRinging =
      status === VOICE_STATUS_RINGING || status === VOICE_STATUS_INCOMING;

    if (!isRinging || this.ringingContactIds.has(contactId)) {
      return;
    }

    this.ringingContactIds.add(contactId);

    this.logger.info(
      `PF RESIZE POC - Chamada tocando. ContactId: ${contactId}. Status: ${voiceContact.status}.`,
    );

    this.onVoiceCallRingingEvent$.next({
      Id: contactId,
      Ani: voiceContact.ani || '',
      Status: voiceContact.status || '',
      IsInbound: voiceContact.isInbound,
    });
  }

  private handleDigitalContact(digitalContact: DigitalContactUpdate): void {
    const caseId = digitalContact?.caseId || '';
    const eventType = digitalContact?.eventDetails?.eventType;

    if (!caseId) {
      return;
    }

    switch (eventType) {
      case DIGITAL_EVENT_CASE_UNASSIGNED: {
        this.dockedDigitalCaseIds.delete(caseId);
        this.lastDockedMessageIdByCase.delete(caseId);
        return;
      }
      case DIGITAL_EVENT_CASE_ASSIGNED: {
        if (this.dockedDigitalCaseIds.has(caseId)) {
          return;
        }

        this.dockedDigitalCaseIds.add(caseId);
        this.emitDigitalInteraction(digitalContact, caseId, eventType);
        return;
      }
      case DIGITAL_EVENT_MESSAGE_ADDED: {
        this.handleCustomerMessage(digitalContact, caseId);
        return;
      }
    }
  }

  /**
   * Só mensagem do cliente conta: a do próprio operador não deve trazer o CIF
   * de volta, já que ele está com a tela aberta para digitar.
   */
  private handleCustomerMessage(
    digitalContact: DigitalContactUpdate,
    caseId: string,
  ): void {
    const messages = digitalContact.messages;
    const lastMessage = messages?.[messages.length - 1];

    if (lastMessage?.direction !== CUSTOMER_MESSAGE_DIRECTION) {
      return;
    }

    const messageId = lastMessage?.id;

    if (messageId) {
      if (this.lastDockedMessageIdByCase.get(caseId) === messageId) {
        return;
      }

      this.lastDockedMessageIdByCase.set(caseId, messageId);
    }

    this.emitDigitalInteraction(
      digitalContact,
      caseId,
      DIGITAL_EVENT_MESSAGE_ADDED,
    );
  }

  private emitDigitalInteraction(
    digitalContact: DigitalContactUpdate,
    caseId: string,
    eventType: string,
  ): void {
    this.logger.info(
      `PF RESIZE POC - Interação digital recebida. CaseId: ${caseId}. EventType: ${eventType}.`,
    );

    this.onDigitalInteractionEvent$.next({
      CaseId: caseId,
      EventType: eventType,
      IsAssignedToAgentInbox: digitalContact.isAssignedToAgentInbox,
      IsCaseAssigned: digitalContact.isCaseAssigned,
    });
  }
}
